import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Insured } from '@/modules/Insured/interfaces/insured'
import { showErrors } from '@/utils/snack'
import { dateTimeToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import ContactDialog from '@/modules/Contact/scripts/contact-dialog'
import InsuredDialog from '@/modules/Insured/scripts/insured-dialog'
import { getInsureds } from '@/modules/Insured/gql/insured.gql'
import { getCaseById } from '@/modules/Case/gql/case.gql'
import { getPolicyById } from '@/modules/Policy/gql/policy.gql'
import { getQuote } from '@/modules/Quote/gql/quote.gql'
import { Case } from '@/modules/Case/interfaces/case'
import { Policy } from '@/modules/Policy/interfaces/policy'
import { Quote } from '@/modules/Quote/interfaces/quote'
import {
  insuredRelationship, relationKeys, relationValues,
  insuredGender, genderKeys, genderValues,
  insuredIncludedOrExcluded, includedKeys, includedValues,
  insuredEducationLevel, educationKeys, educationValues,
  insuredLicenseStatus, licenseKeys, licenseValues,
  insuredMaritalStatus, maritalKeys, maritalValues,
  insuredDUI, duiKeys, duiValues,
  insuredSROrFR, srKeys, srValues,
  insuredHealthClass, healthKeys, healthValues
} from '@/modules/Insured/helpers/insured'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField
  }
})
export default class InsuredList extends mixins(InsuredDialog, ContactDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insureds'
    }
  ]
  searchVal = ''
  policy: Policy | null = null
  quote: Quote | null = null
  caseInfo: Case | null = null
  pathType: string = ''

  insureds: Insured[] = []

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME',
    gender: 'GENDER',
    dateOfBirth: 'DATE_OF_BIRTH',
    healthClass: 'HEALTH_CLASS',
    medications: 'MEDICATIONS',
    relationShip: 'RELATIONSHIP'
  }

  tableColumn = [
    { field: 'name',
      title: 'Insured Name',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', { class: 'text-emphasize' }, row.name ? row.name : '-')
        ]
      }
      },
      sortable: true,
      resizable: true },
    { field: 'relationShip',
      title: 'Relationship',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.relationShip ? insuredRelationship[row.relationShip] : '-')
        ]
      }
      },
      sortable: true,
      resizable: true
    },
    { field: 'gender',
      title: 'Gender',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.gender ? insuredGender[row.gender] : '-')
        ]
      }
      },
      sortable: true,
      resizable: true
    },
    { field: 'dateOfBirth',
      title: 'Date of Birth',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.dateOfBirth ? row.dateOfBirth : '-')
        ]
      }
      },
      sortable: true,
      resizable: true
    },
    { field: 'healthClass',
      title: 'Health Class',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.healthClass ? insuredHealthClass[row.healthClass] : '-')
        ]
      }
      },
      sortable: true,
      resizable: true
    },
    { field: 'medications',
      title: 'Medications',
      slots: { default: ({ row }: any, h: any) => {
        return [
          h('span', row.medications ? row.medications : '-')
        ]
      }
      },
      sortable: true
    },
    { field: 'actions', title: 'Actions', slots: { default: 'action_default' }, fixed: 'right', width: 90 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  insuredRelationship = insuredRelationship
  relationKeys = relationKeys
  relationValues = relationValues
  insuredGender = insuredGender
  genderKeys = genderKeys
  genderValues = genderValues
  insuredIncludedOrExcluded = insuredIncludedOrExcluded
  includedKeys = includedKeys
  includedValues = includedValues
  insuredEducationLevel = insuredEducationLevel
  educationKeys = educationKeys
  educationValues = educationValues
  insuredLicenseStatus = insuredLicenseStatus
  licenseKeys = licenseKeys
  licenseValues = licenseValues
  insuredMaritalStatus = insuredMaritalStatus
  maritalKeys = maritalKeys
  maritalValues = maritalValues
  insuredDUI = insuredDUI
  duiKeys = duiKeys
  duiValues = duiValues
  insuredSROrFR = insuredSROrFR
  srKeys = srKeys
  srValues = srValues
  insuredHealthClass = insuredHealthClass
  healthKeys = healthKeys
  healthValues = healthValues

  dateTimeToDisplay = dateTimeToDisplay

  mounted () {
    this.onResize()
    this.getList()
  }

  async getList () {
    const { params } = this.$route
    const { policyId, quoteId, caseId } = params

    if (policyId) {
      await this.getPolicy(+policyId)
    } else if (quoteId) {
      await this.getQuote(+quoteId)
    } else if (caseId) {
      await this.getCase(+caseId)
    }
    return this.getInsureds()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  routeToDetail (item: any) {
    this.$router.push({
      name: 'InsuredDetailDetail',
      params: {
        id: item.row.id
      }
    })
  }

  async getInsureds () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    const policyId = Number(this.$route.params.policyId)
    const quoteId = Number(this.$route.params.quoteId)
    const caseId = Number(this.$route.params.caseId)
    this.tableOptions.loading = true

    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getInsureds,
        variables: {
          filter: {
            name: searchVal,
            policyId,
            quoteId,
            caseId
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.insureds = result.data.getInsureds.insureds
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = result.data.getInsureds.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getCase (caseId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getCaseById.inquiryName} (Case ID: ${result.data.getCaseById.id})`,
          to: {
            name: 'CaseDetailRelated',
            params: {
              id: result.data.getCaseById.id
            }
          }
        })
      }
      this.insureds = result.data.getCaseById.insureds
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.insureds.length
      this.caseInfo = result.data.getCaseById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getPolicy (policyId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getPolicyById,
        variables: {
          policyId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getPolicyById.policyName} (Policy ID: ${result.data.getPolicyById.id})`,
          to: {
            name: 'PolicyDetailRelated',
            params: {
              id: result.data.getPolicyById.id
            }
          }
        })
      }
      this.insureds = result.data.getPolicyById.insureds
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.insureds.length
      this.policy = result.data.getPolicyById
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getQuote (quoteId: number) {
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getQuote,
        variables: {
          quoteId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      if (this.breadcrumbs.length === 1) {
        this.breadcrumbs.unshift({
          text: `${result.data.getQuote.name} (Quote ID: ${result.data.getQuote.id})`,
          to: {
            name: 'QuoteDetailRelated',
            params: {
              id: result.data.getQuote.id
            }
          }
        })
      }
      this.insureds = result.data.getQuote.insureds
      this.tableOptions.loading = false
      this.tableOptions.tablePage.total = this.insureds.length
      this.quote = result.data.getQuote
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async deleteInsuredConfirm () {
    await this.deleteInsuredPost()
    this.getInsureds()
  }

  refreshList () {
    this.searchVal = ''
    this.getInsureds()
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getInsureds()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getInsureds()
  }
}
